<template>
    <TooltipProvider v-if="tooltipTitle">
        <Tooltip>
            <TooltipTrigger as-child>
                <span class="inline-flex">
                    <component
                        :is="iconComponent"
                        v-bind="$attrs"
                        role="img"
                        :aria-hidden="decorative ? 'true' : undefined"
                        :aria-label="decorative ? undefined : accessibleLabel"
                    />
                </span>
            </TooltipTrigger>
            <TooltipContent>{{ tooltipTitle }}</TooltipContent>
        </Tooltip>
    </TooltipProvider>
    <span v-else class="inline-flex">
        <component
            :is="iconComponent"
            v-bind="$attrs"
            role="img"
            :aria-hidden="decorative ? 'true' : undefined"
            :aria-label="decorative ? undefined : accessibleLabel"
        />
    </span>
</template>

<script setup lang="ts">
defineOptions({
    inheritAttrs: false,
});

import { computed } from 'vue';
import IncidentTypeIconFallback from '@/components/incident/icons/IncidentTypeIconFallback.vue';
import {
    INCIDENT_TYPE_ICON_COMPONENTS,
    isIncidentTypeIconKey,
} from '@/components/incident/icons/incident-type-icon-registry';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatIncidentTypeLabel } from '@/stores/incident';

interface Props {
    iconKey?: string | null;
    decorative?: boolean;
    typeTitle?: string;
    title?: string;
}

const props = withDefaults(defineProps<Props>(), {
    iconKey: '',
    decorative: true,
    typeTitle: '',
    title: undefined,
});

const normalizedIconKey = computed(() => props.iconKey?.trim().toLowerCase() ?? '');
const tooltipTitle = computed(
    () => formatIncidentTypeLabel(props.typeTitle || props.title, normalizedIconKey.value) || undefined,
);

const iconComponent = computed(() => {
    if (!isIncidentTypeIconKey(normalizedIconKey.value)) {
        return IncidentTypeIconFallback;
    }

    return INCIDENT_TYPE_ICON_COMPONENTS[normalizedIconKey.value];
});

const accessibleLabel = computed(() => {
    if (tooltipTitle.value) return tooltipTitle.value;
    if (!normalizedIconKey.value) return 'Incident type icon';
    return `Incident type icon: ${normalizedIconKey.value}`;
});
</script>
